import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";
import { DeviceDisplayNameSetting, Device } from "../Core/Device";

export class DrawSettings implements ObjectSerializer {

	private static readonly DefaultTrimCount = 1;
	private static readonly DefaultTrimChar = '.';

	public ShortDeviceNames: boolean;
	public DeviceTrimLeft: boolean;
	public DeviceTrimRight: boolean;
	public DeviceTrimLeftChar: string;
	public DeviceTrimRightChar: string;
	public DeviceTrimLeftCount: number;
	public DeviceTrimRightCount: number;
	public ShortIfNames: boolean;
	public ShowIpOnIfNames: boolean;

	public StandardPen: PenPattern;
	public LagPen: PenPattern;
	public ManualPen: PenPattern;
	public AssociatedPen: PenPattern;
	public MultiPen: PenPattern;

	public DeviceDisplaySetting: DeviceDisplayNameSetting;
	
	constructor() {
		this.Clear();
	}

	public Clear(): void {
		this.ShortDeviceNames = false;
		this.ShortIfNames = false;
		this.ShowIpOnIfNames = false;
		this.DeviceTrimLeft = false;
		this.DeviceTrimRight = false;
		this.DeviceTrimLeftChar = DrawSettings.DefaultTrimChar;
		this.DeviceTrimRightChar = DrawSettings.DefaultTrimChar;
		this.DeviceTrimLeftCount = DrawSettings.DefaultTrimCount;
		this.DeviceTrimRightCount = DrawSettings.DefaultTrimCount;

		this.StandardPen = new PenPattern();
		this.LagPen = new PenPattern();
		this.LagPen.Color = Color.Gold;
		this.ManualPen = new PenPattern();
		this.ManualPen.Color = Color.Blue;
		this.AssociatedPen = new PenPattern();
		this.AssociatedPen.Color = Color.Orange;
		this.MultiPen = new PenPattern();

		this.DeviceDisplaySetting = DeviceDisplayNameSetting.Default;
	}

	public Write(obj: any, context: any): void {
		obj.draw_settings = {};
		obj = obj.draw_settings;

		obj.short_device_names = ValueEncoder.EncodeBoolean(this.ShortDeviceNames);
		obj.short_if_names = ValueEncoder.EncodeBoolean(this.ShortIfNames);
		obj.show_ip_on_if_names = ValueEncoder.EncodeBoolean(this.ShowIpOnIfNames);
		obj.device_trim_left = ValueEncoder.EncodeBoolean(this.DeviceTrimLeft);
		obj.device_trim_right = ValueEncoder.EncodeBoolean(this.DeviceTrimRight);
		obj.device_trim_left_char = this.DeviceTrimLeftChar;
		obj.device_trim_right_char = this.DeviceTrimRightChar;
		obj.device_trim_left_count = ValueEncoder.EncodeInteger(this.DeviceTrimLeftCount);
		obj.device_trim_right_count = ValueEncoder.EncodeInteger(this.DeviceTrimRightCount);

		context[PenPattern.PenPattern_ObjName_Key] = "standard_pen";
		this.StandardPen.Write(obj, context);
		context[PenPattern.PenPattern_ObjName_Key] = "lag_pen";
		this.LagPen.Write(obj, context);
		context[PenPattern.PenPattern_ObjName_Key] = "manual_pen";
		this.ManualPen.Write(obj, context);
		context[PenPattern.PenPattern_ObjName_Key] = "associated_pen";
		this.AssociatedPen.Write(obj, context);
		context[PenPattern.PenPattern_ObjName_Key] = "multi_pen";
		this.MultiPen.Write(obj, context);

		if (this.DeviceDisplaySetting != DeviceDisplayNameSetting.Default) {
			obj.dev_display_name_setting = DeviceDisplayNameSetting[this.DeviceDisplaySetting];
		}
	}

	public Read(obj: any, context: any): void {
		if (!obj.draw_settings)
			throw new Error();
		obj = obj.draw_settings;

		if (obj.short_device_names !== undefined) {
			this.ShortDeviceNames = ValueEncoder.DecodeBoolean(obj.short_device_names);
		}
		if (obj.short_if_names !== undefined) {
			this.ShortIfNames = ValueEncoder.DecodeBoolean(obj.short_if_names);
		}
		if (obj.show_ip_on_if_names !== undefined) {
			this.ShowIpOnIfNames = ValueEncoder.DecodeBoolean(obj.show_ip_on_if_names);
		}
		if (obj.device_trim_left !== undefined) {
			this.DeviceTrimLeft = ValueEncoder.DecodeBoolean(obj.device_trim_left);
		}
		if (obj.device_trim_right !== undefined) {
			this.DeviceTrimRight = ValueEncoder.DecodeBoolean(obj.device_trim_right);
		}
		if (obj.device_trim_left_char !== undefined) {
			this.DeviceTrimLeftChar = obj.device_trim_left_char;
		}
		if (obj.device_trim_right_char !== undefined) {
			this.DeviceTrimRightChar = obj.device_trim_right_char;
		}
		if (obj.device_trim_left_count !== undefined) {
			this.DeviceTrimLeftCount = ValueEncoder.DecodeInteger(obj.device_trim_left_count);
		}
		if (obj.device_trim_right_count !== undefined) {
			this.DeviceTrimRightCount = ValueEncoder.DecodeInteger(obj.device_trim_right_count);
		}

		if (obj.standard_pen !== undefined) {
			context[PenPattern.PenPattern_ObjName_Key] = "standard_pen";
			this.StandardPen = new PenPattern();
			this.StandardPen.Read(obj, context);			
		}
		if (obj.lag_pen !== undefined) {
			context[PenPattern.PenPattern_ObjName_Key] = "lag_pen";
			this.LagPen = new PenPattern();
			this.LagPen.Read(obj, context);
		}
		if (obj.manual_pen !== undefined) {
			context[PenPattern.PenPattern_ObjName_Key] = "manual_pen";
			this.ManualPen = new PenPattern();
			this.ManualPen.Read(obj, context);
		}
		if (obj.associated_pen !== undefined) {
			context[PenPattern.PenPattern_ObjName_Key] = "associated_pen";
			this.AssociatedPen = new PenPattern();
			this.AssociatedPen.Read(obj, context);
		}
		if (obj.multi_pen !== undefined) {
			context[PenPattern.PenPattern_ObjName_Key] = "multi_pen";
			this.MultiPen = new PenPattern();
			this.MultiPen.Read(obj, context);
		}

		if (obj.dev_display_name_setting !== undefined) {
			this.DeviceDisplaySetting = DeviceDisplayNameSetting[<string>obj.dev_display_name_setting];
		}
	}
}


export class Color {
	public static readonly Gold = new Color(255, 215, 0);
	public static readonly Blue = new Color(0, 0, 255);
	public static readonly Orange = new Color(255, 165, 0);

	public static Parse(s: string): Color {
		if (s.startsWith("#")) {
			let rStr = s.substr(1, 2);
			let gStr = s.substr(3, 2);
			let bStr = s.substr(5, 2);

			let r = Color._FromHexString(rStr);
			let g = Color._FromHexString(gStr);
			let b = Color._FromHexString(bStr);

			return new Color(r, g, b);
		}
		else {
			let regexp = /^(\d+),\s*(\d+),\s*(\d+)$/;
			let match = regexp.exec(s);
			if (match != null) {
				let r = parseInt(match[1]);
				let g = parseInt(match[2]);
				let b = parseInt(match[3]);
				if ((0 <= r && r <= 255) && (0 <= g && g <= 255) && (0 <= b && b <= 255)) {
					return new Color(r, g, b);
				}
				else {
					return null;
				}
			}
			else {
				return null;
			}
		}
	}

	public R: number;
	public G: number;
	public B: number;

	constructor(r: number = 0, g: number = 0, b: number = 0) {
		this.R = r;
		this.G = g;
		this.B = b;
	}

	public ToString(): string {
		return this.R + ", " + this.G + ", " + this.B;
	}

	public ToHexString(): string {
		return "#" +
			Color._ToHexString(this.R) +
			Color._ToHexString(this.G) +
			Color._ToHexString(this.B);
	}

	private static _FromHexString(s: string): number {
		return parseInt(s, 16);
	}

	private static _ToHexString(n: number): string {
		let result: string = n.toString(16);
		switch (result.length) {
			case 0:
				return "00";
			case 1:
				return "0" + result;
			default:
				return result;
		}
	}

}


export enum DashStyle {
	Solid = 0,
	Dash = 1,
	Dot = 2,
	DashDot = 3,
	DashDotDot = 4
};


export class PenPattern implements ObjectSerializer {

	public Color: Color;
	public Width: number;
	public DashStyle: DashStyle;

	constructor() {
		this.Clear();
	}

	public Clear(): void {
		this.Color = new Color(0, 0, 0);
		this.Width = 1;
		this.DashStyle = DashStyle.Solid;
	}

	public static readonly PenPattern_ObjName_Key = "PenPattern.ObjName";

	public Write(obj: any, context: any): void {
		let objName = "pen_pattern";
		if (context && context[PenPattern.PenPattern_ObjName_Key]) {
			objName = context[PenPattern.PenPattern_ObjName_Key];
		}

		obj[objName] = {};
		obj = obj[objName];

		obj.color = this.Color.ToString();
		obj.width = ValueEncoder.EncodeInteger(this.Width);
		obj.dash_style = DashStyle[this.DashStyle];
	}

	public Read(obj: any, context: any): void {
		let objName = "pen_pattern";
		if (context && context[PenPattern.PenPattern_ObjName_Key]) {
			objName = context[PenPattern.PenPattern_ObjName_Key];
		}

		if (!obj[objName])
			throw new Error();
		obj = obj[objName];

		if (obj.color !== undefined) {
			this.Color = Color.Parse(obj.color);
		}
		if (obj.width !== undefined) {
			this.Width = ValueEncoder.DecodeInteger(obj.width);
		}
		if (obj.dash_style !== undefined) {
			this.DashStyle = DashStyle[<string>obj.dash_style];
		}
	}
}
